import store from "../../data/store/store";
import { Quaternion } from "@babylonjs/core";
import {
  evalArr as baseEvalArr,
  expandIterated,
  splitTopLevel,
  extractRawList,
  parseConditionalPosition,
  resolveSettingValue
} from "../../modules/components/scriptsPageutil/evalArr";
// láncolt makró feloldása: (*68*) -> érték; ha az "(*17*)" akkor továbblép rekurzívan
const DBG = false; // kapcsold ki/ki ha túl zajos
const DBG_REF = false; // ha a ref feloldásról is kell részletes infó
// Félértékre végződőt kerekítünk egészre (10.5 -> 11, -3.5 -> -3), különben változatlanul hagyjuk
function roundToInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0; // mindig egészre (0.5 → felfelé, -0.5 → -0)
}
const roundSizeTriple = ([w, h, d]) => [
  roundToInt(w),
  roundToInt(h),
  roundToInt(d)
];
const DUPLUNG_SCRIPT_ID = 49;
let duplungInstanceCounter = 0; // D1, D2, ...

function isDuplungScript(sid) {
  if (String(sid) === String(DUPLUNG_SCRIPT_ID)) return true;
  try {
    const st = store.getState() || {};
    const scripts =
      st.scripts || st.allScripts || st.scriptsList || st.all_scripts || [];
    const scr = scripts.find((s) => String(s.scriptId) === String(sid)) || null;
    return (scr?.name || "").toLowerCase() === "standard duplung";
  } catch {
    return false;
  }
}

function dlog(...args) {
  if (DBG) console.log(...args);
}
function dgroup(label) {
  if (DBG) console.groupCollapsed(label);
}
function dgroupEnd() {
  if (DBG) console.groupEnd();
}
function evaluateFormula(formula, settings, instanceIndex = null) {
  const arr = Array.isArray(settings)
    ? settings
    : Object.entries(settings || {}).map(([settingId, value]) => ({
        settingId: String(settingId),
        value: String(value)
      }));

  const S = Object.fromEntries(arr.map((s) => [s.settingId, s.value]));

  const getVal = (key) => {
    if (Object.prototype.hasOwnProperty.call(S, key)) {
      return S[key];
    }
    return resolveSettingValue(arr, key);
  };

  function formatValueForExpr(raw) {
    if (raw == null) return "null";

    if (typeof raw === "number") {
      return Number.isFinite(raw) ? String(raw) : "0";
    }

    if (typeof raw === "boolean") {
      return raw ? "true" : "false";
    }

    const s = String(raw).trim();

    if (s === "") return `""`;
    if (/^-?\d+(\.\d+)?$/.test(s)) return s;
    if (s === "true" || s === "false" || s === "null") return s;

    return JSON.stringify(s);
  }

  function getIndexedValue(raw, idx) {
    if (idx == null) return raw;

    try {
      const parsed = JSON.parse(String(raw));
      if (Array.isArray(parsed) && !parsed.some(Array.isArray)) {
        return parsed[idx] ?? parsed[parsed.length - 1] ?? parsed[0] ?? 0;
      }
    } catch {}

    return raw;
  }

  let expr = String(formula).trim();

  expr = expr.replace(
    /\(\*(\d+)(?:@(\d+))?(?:\.(cur|sum|len|idx|idx0|bef\.sum|aft\.sum))?\*\)/g,
    (_, id, sec, op) => {
      const k = sec ? `${id}@${sec}` : id;
      const raw = getVal(k);

      if (op) {
        const meta = getIndexedSettingMeta(raw, instanceIndex ?? 0);
        if (!meta) {
          return formatValueForExpr(0);
        }

        switch (op) {
          case "cur":
            return formatValueForExpr(meta.cur);
          case "sum":
            return formatValueForExpr(meta.sum);
          case "len":
            return formatValueForExpr(meta.len);
          case "idx":
            return formatValueForExpr(meta.idx);
          case "idx0":
            return formatValueForExpr(meta.idx0);
          case "bef.sum":
            return formatValueForExpr(meta.befSum);
          case "aft.sum":
            return formatValueForExpr(meta.aftSum);
          default:
            return formatValueForExpr(0);
        }
      }

      const val = getIndexedValue(raw, instanceIndex);
      return formatValueForExpr(val);
    }
  );

  expr = expr.replace(/\b(width|height|depth)\b/g, (_, k) => {
    const v = S[k] != null ? S[k] : 0;
    return formatValueForExpr(v);
  });

  expr = expr.replace(/[+\-*/\s]+$/, "");
  return expr;
}
function getRawSettingValue(settings, key) {
  if (!settings) return undefined;

  if (!Array.isArray(settings)) {
    if (Object.prototype.hasOwnProperty.call(settings, key)) {
      return settings[key];
    }
    return undefined;
  }

  const hit = settings.find((s) => String(s.settingId) === String(key));
  return hit ? hit.value : undefined;
}

function formulaUsesIndexedSetting(formula, settings) {
  const macros =
    String(formula || "").match(
      /\(\*(\d+)(?:@(\d+))?(?:\.(cur|sum|len|idx|idx0|bef\.sum|aft\.sum))?\*\)/g
    ) || [];

  for (const macro of macros) {
    const m = macro.match(
      /\(\*(\d+)(?:@(\d+))?(?:\.(cur|sum|len|idx|idx0|bef\.sum|aft\.sum))?\*\)/
    );
    if (!m) continue;

    const key = m[2] ? `${m[1]}@${m[2]}` : m[1];
    const raw = getRawSettingValue(settings, key);

    if (raw == null) continue;

    try {
      const parsed = JSON.parse(String(raw));
      if (Array.isArray(parsed) && !parsed.some(Array.isArray)) {
        return true;
      }
    } catch {}
  }

  return false;
}

function getIndexedSettingMeta(raw, instanceIndex = 0) {
  try {
    const parsed = JSON.parse(String(raw));
    if (!Array.isArray(parsed) || parsed.some(Array.isArray)) return null;

    const nums = parsed.map((v) => Number(v) || 0);
    const idx = Math.max(0, Math.min(instanceIndex, nums.length - 1));

    return {
      list: nums,
      len: nums.length,
      idx0: idx,
      idx: idx + 1,
      cur: nums[idx] ?? 0,
      bef: nums.slice(0, idx),
      aft: nums.slice(idx + 1),
      befSum: nums.slice(0, idx).reduce((a, b) => a + b, 0),
      aftSum: nums.slice(idx + 1).reduce((a, b) => a + b, 0),
      sum: nums.reduce((a, b) => a + b, 0)
    };
  } catch {
    return null;
  }
}
function buildLocalIndexedCfg(cfg, instanceIndex) {
  const local = { ...cfg };

  Object.keys(local).forEach((key) => {
    const raw = local[key];

    try {
      const parsed = JSON.parse(String(raw));
      if (Array.isArray(parsed) && !parsed.some(Array.isArray)) {
        const picked =
          parsed[instanceIndex] ?? parsed[parsed.length - 1] ?? parsed[0] ?? 0;

        local[key] = picked;
      }
    } catch {}
  });

  return local;
}

function normalizeTriple(result) {
  if (!Array.isArray(result)) return [0, 0, 0];

  if (Array.isArray(result[0])) {
    return result[0].slice(0, 3).map((n) => Number(n) || 0);
  }

  return result.slice(0, 3).map((n) => Number(n) || 0);
}

function evalTripleExpression(expr) {
  try {
    const result = new Function(`return (${expr});`)();
    return normalizeTriple(result);
  } catch {
    return null;
  }
}

function buildPerInstanceTriples(formula, cfg, count, dimsTriple) {
  const out = [];

  for (let i = 0; i < count; i++) {
    const expr = evaluateFormula(formula, cfg, i).trim();

    let triple = evalTripleExpression(expr);

    if (!triple) {
      const localCfg = buildLocalIndexedCfg(cfg, i);
      const localDims = [
        Number(localCfg.width ?? dimsTriple[0] ?? 0),
        Number(localCfg.height ?? dimsTriple[1] ?? 0),
        Number(localCfg.depth ?? dimsTriple[2] ?? 0)
      ];

      try {
        const viaEvalArr = evalArr(expr, localCfg, localDims);
        triple = normalizeTriple(viaEvalArr);
      } catch {}
    }

    if (!triple) {
      const parsed = parseBracketedArrays(expr);
      triple = parsed[0] || [0, 0, 0];
    }

    out.push(triple);
  }

  return out;
}

function uniqDefs(arr) {
  const seen = new Set();
  const out = [];
  for (const it of arr || []) {
    // biztos kulcs: scriptId + (itemId || scriptItemId)
    const key = `${it.scriptId}|${it.itemId ?? it.scriptItemId ?? it.name}`;
    if (seen.has(key)) {
      if (DBG) console.warn("⚠️ DUP DEF dropped:", key, it.name);
      continue;
    }
    seen.add(key);
    out.push(it);
  }
  return out;
}

function parseRepeatConfig(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s.startsWith("repeat:")) return null;
  const parts = s
    .slice("repeat:".length)
    .split(";")
    .map((seg) => seg.split("=").map((x) => x.trim()))
    .filter(([k, v]) => k && v);
  const m = Object.fromEntries(parts);
  return {
    source: m.source ? Number(m.source) : null,
    countId: m.count || null
  };
}

// --- HELPERS: refScriptId feloldása (szám, makró, kifejezés) ---
/*function resolveRefScriptId(refRaw, cfg, sectionIndex = null) {
  if (DBG_REF)
    dlog("🔗 resolveRefScriptId IN:", {
      refRaw,
      sectionIndex,
      peek: Object.keys(cfg || {}).slice(0, 8)
    });

  if (refRaw == null || refRaw === "") return null;
  let raw = String(refRaw).trim();

  if (
    (raw.startsWith('"') && raw.endsWith('"')) ||
    (raw.startsWith("'") && raw.endsWith("'"))
  ) {
    raw = raw.slice(1, -1);
  }
  if (/^\d+$/.test(raw)) return raw;

  // két csoport: id és opcionális @sec
  const m = raw.match(/^\(\*(\d+)(?:@(\d+))?\*\)$/);
  if (m) {
    let key = m[1];
    const secInMacro = m[2] ? Number(m[2]) : null;

    // ha nincs sec a makróban, de repeater-érték van cfg-ben → 71@{sectionIndex}
    if (!secInMacro && sectionIndex != null) {
      const cand = `${key}@${sectionIndex}`;
      if (cfg && Object.prototype.hasOwnProperty.call(cfg, cand)) key = cand;
    }

    const settingsArr = Object.entries(cfg || {}).map(([settingId, value]) => ({
      settingId: String(settingId),
      value: String(value)
    }));
    let resolved = resolveSettingValue(settingsArr, key); // lehet pl. "103" vagy "\"103\""
    try {
      resolved = JSON.parse(resolved);
    } catch {}
    const ret = /^\d+$/.test(String(resolved)) ? String(resolved) : null;
    if (DBG_REF)
      dlog("🔗 resolveRefScriptId MACRO", {
        raw,
        key,
        secInMacro,
        sectionIndex,
        resolved,
        ret
      });
    return ret;
  }

  try {
    const expr = evaluateFormula(raw, cfg);
    const v = new Function(`return (${expr});`)();
    if (DBG_REF) dlog("🔗 resolveRefScriptId EXPR", { raw, expr, v });
    return v != null ? String(v) : null;
  } catch {
    if (DBG_REF) dlog("🔗 resolveRefScriptId ERROR", { raw });
    return null;
  }
}*/
function resolveRefScriptId(refRaw, cfg, sectionIndex = null) {
  if (DBG_REF) dlog("🔗 resolveRefScriptId IN:", { refRaw, sectionIndex });

  if (refRaw == null || refRaw === "") return null;
  let raw = String(refRaw).trim();

  // külső idézők le
  if (
    (raw.startsWith('"') && raw.endsWith('"')) ||
    (raw.startsWith("'") && raw.endsWith("'"))
  )
    raw = raw.slice(1, -1);

  // tiszta szám
  if (/^\d+$/.test(raw)) return raw;

  // TISZTA MAKRÓ: (*71*) vagy (*71@2*)
  const m = raw.match(/^\(\*(\d+)(?:@(\d+))?\*\)$/);
  if (m) {
    const baseId = m[1];
    const secInMacro = m[2] ? Number(m[2]) : null;

    const settingsArr = Object.entries(cfg || {}).map(([settingId, value]) => ({
      settingId: String(settingId),
      value: String(value)
    }));

    // ha nincs @ a makróban, de a hívó adott szekciót és van ilyen kulcsunk, árnyékoljuk
    const key = secInMacro
      ? `${baseId}@${secInMacro}`
      : sectionIndex != null &&
          Object.prototype.hasOwnProperty.call(cfg, `${baseId}@${sectionIndex}`)
        ? `${baseId}@${sectionIndex}`
        : baseId;

    let resolved = resolveSettingValue(settingsArr, key);
    try {
      resolved = JSON.parse(resolved);
    } catch {}
    const ret = /^\d+$/.test(String(resolved)) ? String(resolved) : null;

    if (DBG_REF)
      dlog("🔗 resolveRefScriptId MACRO", { raw, key, resolved, ret });
    return ret;
  }

  // ÁLTALÁNOS KIFEJEZÉS:
  // itt jön a LÉNYEG: ha van sectionIndex, akkor a „csupasz” (*N*) makrókat
  // a helyi (*N@sec*) értékkel árnyékoljuk, hogy az expr helyesen döntsön
  try {
    const local = { ...cfg };
    if (sectionIndex != null) {
      const bareRe = /\(\*(\d+)\*\)/g;
      let mm;
      const touched = new Set();
      while ((mm = bareRe.exec(raw))) {
        const id = mm[1];
        const secKey = `${id}@${sectionIndex}`;
        if (local[secKey] != null && local[id] == null && !touched.has(id)) {
          local[id] = local[secKey]; // árnyékolás
          touched.add(id);
        }
      }
    }
    const expr = evaluateFormula(raw, local);
    const v = new Function(`return (${expr});`)();
    if (DBG_REF) dlog("🔗 resolveRefScriptId EXPR", { raw, expr, v });
    return v != null ? String(v) : null;
  } catch {
    if (DBG_REF) dlog("🔗 resolveRefScriptId ERROR", { raw });
    return null;
  }
}

function parseBracketedArrays(str) {
  // 1) Távolítsuk el a külső idézőjeleket
  if (
    (str.startsWith(`"`) && str.endsWith(`"`)) ||
    (str.startsWith(`'`) && str.endsWith(`'`))
  ) {
    str = str.slice(1, -1);
  }

  // 2) Teljes ternary JS-eval
  if (str.includes("?")) {
    try {
      let expr = str.trim();
      if (expr.startsWith("[") && expr.endsWith("]")) {
        expr = expr.slice(1, -1);
      }
      const result = new Function(`return (${expr});`)();
      // Ha nested tömb, akkor egyből visszaadjuk
      if (Array.isArray(result[0])) {
        const mapped = result.map((r) => r.map((v) => Number(v) || 0));
        return mapped;
      } else {
        const single = [result.map((v) => Number(v) || 0)];
        return single;
      }
    } catch (err) {
      return [];
    }
  }

  // 3) Ha a teljes str dupla-nested, próbáljuk meg egyszerre JS-eval-lel
  const trimmed = str.trim();
  if (trimmed.startsWith("[[") && trimmed.endsWith("]]")) {
    try {
      const result = new Function(`return ${trimmed};`)();
      if (Array.isArray(result) && Array.isArray(result[0])) {
        const mapped = result.map((r) => r.map((v) => Number(v) || 0));
        return mapped;
      }
    } catch (err) {
      console.error(
        "   ❌ parseBracketedArrays full-nested hiba:",
        err,
        trimmed
      );
      // esés tovább a regex-re
    }
  }

  // 4) Végül jöhet a sima regexes [x,y,z] kigyűjtés
  const arrays = [];
  const bracketRegex = /\[([^\]]+)\]/g;
  let match;
  while ((match = bracketRegex.exec(str)) !== null) {
    try {
      const result = new Function(`return ${match[0]};`)();
      if (Array.isArray(result)) {
        const row = result.slice(0, 3).map((v) => Number(v) || 0);
        arrays.push(row);
      } else {
      }
    } catch (err) {
      console.error("   ❌ parseBracketedArrays eval hiba for", match[0], err);
    }
  }

  return arrays;
}

function parseSettingString(str) {
  return str
    ? str
        .split(",")
        .map((p) => {
          const i = p.indexOf(":");
          if (i === -1) return null;

          const id = p.slice(0, i).trim();
          const val = p.slice(i + 1).trim();

          return id ? { settingId: id, value: val } : null;
        })
        .filter(Boolean)
    : [];
}
function quatMul(a, b) {
  const [ax, ay, az, aw] = a,
    [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz
  ];
}
function quatConjugate([x, y, z, w]) {
  return [-x, -y, -z, w];
}
function eulerUnitToQuat([ux, uy, uz]) {
  const pitch = ux * (Math.PI / 2),
    yaw = uy * (Math.PI / 2),
    roll = uz * (Math.PI / 2);
  const cy = Math.cos(yaw / 2),
    sy = Math.sin(yaw / 2);
  const cp = Math.cos(pitch / 2),
    sp = Math.sin(pitch / 2);
  const cr = Math.cos(roll / 2),
    sr = Math.sin(roll / 2);
  const qYaw = [0, sy, 0, cy];
  const qPitch = [sp, 0, 0, cp];
  const qRoll = [0, 0, sr, cr];
  return quatMul(quatMul(qYaw, qPitch), qRoll);
}
function quatToEulerUnit(q) {
  const [x, y, z, w] = q;
  const sinr = 2 * (w * x + y * z),
    cosr = 1 - 2 * (x * x + y * y);
  const roll = Math.atan2(sinr, cosr);
  let sinp = 2 * (w * y - z * x);
  sinp = Math.max(-1, Math.min(1, sinp));
  const pitch = Math.asin(sinp);
  const siny = 2 * (w * z + x * y),
    cosy = 1 - 2 * (y * y + z * z);
  const yaw = Math.atan2(siny, cosy);
  return [
    Math.round(roll / (Math.PI / 2)) % 4,
    Math.round(pitch / (Math.PI / 2)) % 4,
    Math.round(yaw / (Math.PI / 2)) % 4
  ];
}
function rotateVec(q, v) {
  const [x, y, z] = v,
    qv = [x, y, z, 0];
  return quatMul(quatMul(q, qv), quatConjugate(q)).slice(0, 3);
}
// a fájl tetején (a többi helper mellé)
function parseSettingToObject(str) {
  const arr = parseSettingString(str);
  return arr.reduce((acc, { settingId, value }) => {
    acc[String(settingId)] = value;
    return acc;
  }, {});
}

function getScriptDefaultsById(scriptId) {
  const st = store.getState() || {};
  // alkalmazkodjunk a state szerkezetéhez
  const scripts =
    st.scripts || st.allScripts || st.scriptsList || st.all_scripts || [];
  const scr =
    scripts.find((s) => String(s.scriptId) === String(scriptId)) || null;
  if (!scr || !scr.setting) return {};
  return parseSettingToObject(scr.setting);
}

/**
 * Main entry: generates the final item list recursively.
 */
function processScript(
  currentConfig,
  measurements,
  scriptId,
  overrideItems = null
) {
  const __ps_in = { currentConfig, measurements, scriptId, overrideItems };

  const state = store.getState();
  const rawDefs =
    (overrideItems && overrideItems.length
      ? overrideItems
      : state.scriptItems) || [];
  const defs = uniqDefs(rawDefs);

  const { width, height, depth } = measurements;

  dlog("📚 defs loaded:", {
    raw: rawDefs.length,
    unique: defs.length,
    scripts: [...new Set(defs.map((d) => d.scriptId))].length
  });

  const work = state.selectedWork || null;

  const seenRoot = new Set();
  function gen(
    id,
    cfg,
    groupPath = [],
    instancePath = [],
    groupRootId = null,
    lvl = 0,
    seen = seenRoot
  ) {
    if (lvl > 20) {
      console.warn("Max depth reached", { id });
      return [];
    }
    if (seen.has(String(id))) {
      console.warn("Script cycle detected, skipping", { id, path: [...seen] });
      return [];
    }
    const seenNext = new Set(seen);
    seenNext.add(String(id));

    const indent = "  ".repeat(lvl);
    const itemsRaw = defs.filter((it) => String(it.scriptId) === String(id));
    // ugyanazon scripten belüli duplák kiszűrése
    const seenInScript = new Set();
    const items = itemsRaw.filter((it) => {
      const k =
        it.itemId ??
        it.scriptItemId ??
        `${it.name}|${it.material}|${it.size}|${it.qty}`;
      if (seenInScript.has(k)) return false;
      seenInScript.add(k);
      return true;
    });
    dlog(
      `   defs for script ${id}: raw=${itemsRaw.length} uniq=${items.length}`
    );

    let out = [];
    function splitRefRaw(raw) {
      if (!raw) return ["", null];
      const s = String(raw).trim();
      let depthPar = 0,
        depthBr = 0;
      for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === "(") depthPar++;
        else if (ch === ")") depthPar--;
        else if (ch === "[") depthBr++;
        else if (ch === "]") depthBr--;
        else if (ch === "|" && depthPar === 0 && depthBr === 0) {
          return [s.slice(0, i).trim(), s.slice(i + 1).trim()];
        }
      }
      return [s, null];
    }

    function parseRefModifiers(hintRaw) {
      const mods = { split: false, group: null, shallow: false, merge: false };
      if (!hintRaw) return mods;

      const tokens = hintRaw
        .split(/[;,]/)
        .map((t) => t.trim())
        .filter(Boolean);

      for (const tok of tokens) {
        const [k0, v0] = tok.split(/[:=]/).map((s) => s?.trim());
        const k = (k0 || "").toLowerCase();
        const v = (v0 ?? "true").toLowerCase();

        const truthy = ["true", "1", "yes", "igen"].includes(v);
        const falsy = ["false", "0", "no", "nem"].includes(v);

        if (
          k === "split" ||
          k === "perinstance" ||
          (k === "ovr" && v === "inst")
        ) {
          mods.split = truthy || v0 == null;
        } else if (k === "group" || k === "group:") {
          mods.group = v0 == null ? "" : v0;
        } else if (
          k === "shallow" ||
          (k === "expand" && falsy) ||
          k === "flat"
        ) {
          mods.shallow = true;
        } else if (k === "merge" || k === "aggregate" || k === "collapse") {
          mods.merge = truthy || v0 == null;
        }
      }
      return mods;
    }

    items.forEach((item) => {
      dgroup(
        `🧩 L${lvl} Script#${id} → item#${
          item.scriptItemId ?? item.itemId ?? "?"
        } "${item.name}"`
      );
      try {
        const overrideArr = parseSettingString(item.refSettings);
        const overrideRaw = overrideArr.reduce((acc, { settingId, value }) => {
          const n = Number(value);
          acc[String(settingId)] = Number.isFinite(n) ? n : value;
          return acc;
        }, {});
        // ⬇⬇⬇ NE engedjük, hogy a refSettings dimenziói felülírják a futó script dimenzióit
        const {
          width: _ow,
          height: _oh,
          depth: _od,
          ...overrideNoDims
        } = overrideRaw;

        const C = { ...overrideNoDims, ...cfg };

        dlog("   cfg (merge):", C);
        if (item.itemId === 266) {
          // "Fiókos oldal"
          console.log("266 DBG", {
            _rawSize: item.size,
            c72: C["72"],
            sizeAfter: evaluateFormula(item.size, C)
          });
        }
        if (item.itemId === 267) {
          // "Fiókok"
          console.log("267 DBG", {
            qtyRaw: item.qty,
            qtyAfter: new Function(
              `return (${evaluateFormula(item.qty, C)})`
            )(),
            sizeAfter: evaluateFormula(item.size, C)
          });
        }

        // compute quantity
        const qtyExpr = evaluateFormula(item.qty, C);
        let rawQ;
        try {
          rawQ = new Function(`return (${qtyExpr});`)();
        } catch (e) {
          console.error(`${indent}Error evaluating qty:`, e, qtyExpr);
          rawQ = 0;
        }
        const Q = Math.round(Number(rawQ));
        dlog(`   QTY: expr="${qtyExpr}" → raw=${rawQ} → Q=${Q}`);

        const rawFromDef =
          [
            item.refScriptExpr,
            item.refScriptId,
            item.refScript,
            item.ref_script_expr,
            item.ref_script_id
          ]
            .map((v) => (v == null ? "" : String(v).trim()))
            .find((s) => s.length > 0) || null;
        // alapértelmezett: annyi példány, amennyit a Q ad
        let instTotal = Q;
        let refExpr = null,
          mods = { split: false, group: null, shallow: false, merge: false },
          macroIdBase = null;
        if (rawFromDef) {
          const split = splitRefRaw(rawFromDef);
          refExpr = split[0];
          mods = parseRefModifiers(split[1]);
          const mm = String(refExpr).match(/^\(\*(\d+)(?:@\d+)?\*\)$/);
          macroIdBase = mm ? Number(mm[1]) : null;
          if (macroIdBase != null) {
            const sectionKeys = Object.keys(C).filter((k) =>
              k.startsWith(`${macroIdBase}@`)
            );
            const countId = parseRepeatConfig(C[macroIdBase])?.countId || null;
            const nFromCountId = countId ? parseInt(C[countId], 10) || 0 : 0;
            const nSections = sectionKeys.length || nFromCountId || 0;
            if (nSections > 0) instTotal = nSections;
          }
        }
        if ((!rawFromDef && Q <= 0) || (rawFromDef && instTotal <= 0)) {
          dlog("   quantity/instTotal is 0 → skip");
          return; // a finally úgyis lezárja a groupot
        }
        // --- és MOST jöhetnek a size/pos/rot tömbök, instTotal alapján ---
        const dimsTriple = [C.width, C.height, C.depth];
        const settingsArr = Object.entries(C).map(([settingId, value]) => ({
          settingId,
          value: String(value)
        }));

        const sizeFormula = String(item.size || "");
        const posFormula = String(item.position || "");
        const rotFormula = String(item.rotation || "");

        const sizeUsesI = /\bi\(/.test(sizeFormula);
        const posUsesI = /\bi\(/.test(posFormula);
        const rotUsesI = /\bi\(/.test(rotFormula);

        const sizeUsesIndexed = formulaUsesIndexedSetting(sizeFormula, C);
        const posUsesIndexed = formulaUsesIndexedSetting(posFormula, C);
        const rotUsesIndexed = formulaUsesIndexedSetting(rotFormula, C);

        const sizes =
          sizeUsesIndexed && !sizeUsesI
            ? buildPerInstanceTriples(sizeFormula, C, instTotal, dimsTriple)
            : parseConditionalPosition(
                evaluateFormula(sizeFormula, C),
                settingsArr,
                dimsTriple,
                sizeUsesI ? instTotal : 1
              );

        const posArr =
          posUsesIndexed && !posUsesI
            ? buildPerInstanceTriples(posFormula, C, instTotal, dimsTriple)
            : parseConditionalPosition(
                evaluateFormula(posFormula, C),
                settingsArr,
                dimsTriple,
                instTotal
              );

        const rotArr =
          rotUsesIndexed && !rotUsesI
            ? buildPerInstanceTriples(rotFormula, C, instTotal, dimsTriple)
            : parseConditionalPosition(
                evaluateFormula(rotFormula, C),
                settingsArr,
                dimsTriple,
                instTotal
              );

        const rawPosList = extractRawList(evaluateFormula(posFormula, C));
        let fallbackPosArr =
          rawPosList.length > 0
            ? rawPosList.map((str) => evalArr(`[${str}]`, C, dimsTriple))
            : [[0, 0, 0]];

        const rawRotList = extractRawList(evaluateFormula(rotFormula, C));
        let fallbackRotArr =
          rawRotList.length > 0
            ? rawRotList.map((str) => evalArr(`[${str}]`, C, dimsTriple))
            : [[0, 0, 0]];
        dlog(`   SIZES: count=${sizes.length}`, sizes.slice(0, 3));
        dlog(`   POS: count=${posArr.length}`, posArr.slice(0, 3));
        dlog(`   ROT: count=${rotArr.length}`, rotArr.slice(0, 3));
        // ---------------------------------------------------------------------------

        function parseRepeatConfig(raw) {
          if (!raw) return null;
          const s = String(raw).trim();
          if (!s.startsWith("repeat:")) return null;
          const parts = s
            .slice("repeat:".length)
            .split(";")
            .map((seg) => seg.split("=").map((x) => x.trim()))
            .filter(([k, v]) => k && v);
          const m = Object.fromEntries(parts);
          return {
            source: m.source ? Number(m.source) : null,
            countId: m.count || null
          };
        }

        if (rawFromDef) {
          // refExpr / mods / macroIdBase / instTotal már ki van számolva fentebb

          const isPlainId = /^\d+$/.test(refExpr);
          const iterates =
            posUsesI || rotUsesI || posUsesIndexed || rotUsesIndexed;
          if (!mods.shallow && isPlainId && (instTotal > 1 || iterates)) {
            mods = { ...mods, shallow: true };
          }

          dlog("   REF raw:", rawFromDef);
          dlog("   REF parsed:", { refExpr, mods, instTotal, macroIdBase });

          const isPlainIdDuplung =
            /^\d+$/.test(refExpr) && isDuplungScript(Number(refExpr));
          const duAnchorNoMacroBase = isPlainIdDuplung
            ? `${groupPath.join("|")}|CALL:${
                item.scriptItemId ?? item.itemId ?? "X"
              }→${refExpr}`
            : null;

          for (let sec = 1; sec <= instTotal; sec++) {
            const j = sec - 1;

            const szRaw = sizes[j] ||
              sizes[j % Math.max(1, sizes.length)] || [
                C.width,
                C.height,
                C.depth
              ];
            const sz = roundSizeTriple(szRaw);
            const [pw, ph, pd] = sz;
            if (String(item.itemId) === "463") {
              console.log("PARENT DRAWER SIZE DEBUG", {
                sec,
                szRaw,
                sz,
                C,
                sizeFormula
              });
            }
            const parentBase = { ...C, width: pw, height: ph, depth: pd };
            const nid = resolveRefScriptId(refExpr, parentBase, sec);
            if (!nid) continue;

            const childDefaultsRaw = getScriptDefaultsById(nid);
            const {
              width: _dw,
              height: _dh,
              depth: _dd,
              ...childDefaults
            } = childDefaultsRaw;

            const childOvr = (() => {
              const out = {};
              // 1) SCRIPT-SZINTŰ: mindig
              const scrPrefix = `__child__:${Number(nid)}:`;
              Object.entries(currentConfig || {}).forEach(([k, v]) => {
                if (k.startsWith(scrPrefix)) out[k.slice(scrPrefix.length)] = v;
              });
              // 2) PÉLDÁNY-SZINTŰ: csak ha split
              if (mods.split && macroIdBase != null) {
                const instPrefix = `__child__:${macroIdBase}@${sec}:`;
                Object.entries(currentConfig || {}).forEach(([k, v]) => {
                  if (k.startsWith(instPrefix))
                    out[k.slice(instPrefix.length)] = v;
                });
              }
              return out;
            })();

            const childCfgBase = {
              ...childDefaults,
              ...overrideNoDims,
              ...parentBase,
              ...childOvr
            };
            if (String(nid) === "50") {
              console.log("CHILD CFG DEBUG", {
                sec,
                parentBase,
                childCfgBase
              });
            }
            if (macroIdBase != null) {
              const secKey = `${macroIdBase}@${sec}`;
              if (
                childCfgBase[macroIdBase] == null &&
                childCfgBase[secKey] != null
              ) {
                childCfgBase[macroIdBase] = childCfgBase[secKey];
              }
            }
            // ⬇⬇⬇ EZ HIÁNYZOTT → definiáld a childGroup-ot
            const childGroupPath = [...groupPath, sec];
            const childInstancePath = [...instancePath, sec];
            const children = gen(
              nid,
              childCfgBase,
              childGroupPath,
              childInstancePath,
              groupRootId,
              lvl + 1,
              seenNext
            );
            const isDuplung = isDuplungScript(nid);
            // DU token: azonos maradjon ugyanazon makró-szekció alatt,
            // és különbözzön más szekciók/hívások között (NE függjön a size-index-től)
            let duplungToken = null;
            if (isDuplung) {
              // ha a ref kifejezés makrót használ (pl. (*71*)/@sec), akkor a makró id + sec az anchor
              // különben adjunk stabil, de hívásonként eltérő fallback kulcsot
              const duAnchor =
                macroIdBase != null
                  ? `${groupPath.join("|")}|${macroIdBase}@${sec}`
                  : (duAnchorNoMacroBase ??
                    `${groupPath.join("|")}|DU#${++duplungInstanceCounter}`);
              duplungToken = `DU:${duAnchor}`;
            }
            const P = posArr[j] || posArr[0] || fallbackPosArr[0] || [0, 0, 0];
            const pu = rotArr[j] || rotArr[0] || fallbackRotArr[0] || [0, 0, 0];

            const pQ = eulerUnitToQuat(pu);
            const [px, py, pz] = pu.map((u) => (u * Math.PI) / 2);
            const pQ1 = Quaternion.RotationYawPitchRoll(py, px, pz);
            children.forEach((ci, ciIndex) => {
              const basePosList = parseBracketedArrays(ci.position);
              const baseRotList = parseBracketedArrays(ci.rotation);
              const childCount = Math.max(1, Number(ci.qty) || 1);

              const posList = [];
              const rotList = [];
              const groupIdList = [];
              for (let k = 0; k < childCount; k++) {
                const itemKey = item.scriptItemId ?? item.itemId ?? "X";
                const groupKey = [...childGroupPath, itemKey].join("|");
                const effectiveGroupId =
                  instTotal > 1
                    ? `INST:${groupKey}|${sec}`
                    : `INST:${groupKey}`;

                const origPos = basePosList[k] || basePosList[0] || [0, 0, 0];
                const tp = rotateVec(pQ, origPos);
                const shifted = tp.map((c, i) => c + P[i]);
                posList.push(`[${shifted.join(",")}]`);
                groupIdList.push(`[${shifted.join(",")},${effectiveGroupId}]`);
                const cu = baseRotList[k] || baseRotList[0] || [0, 0, 0];
                const [cx, cy, cz] = cu.map((u) => (u * Math.PI) / 2);
                const cQ = Quaternion.RotationYawPitchRoll(cy, cx, cz);
                const combo = pQ1.multiply(cQ);
                const e = combo.toEulerAngles();
                const ur = Math.round(e.x / (Math.PI / 2)) % 4;
                const up = Math.round(e.y / (Math.PI / 2)) % 4;
                const uy = Math.round(e.z / (Math.PI / 2)) % 4;
                rotList.push(`[${ur},${up},${uy}]`);
              }
              out.push({
                ...ci,
                qty: childCount,
                position: posList.join(","),
                rotation: rotList.join(","),
                groupId: groupIdList.join(","),
                instancePath: childInstancePath.join("|"),
                __duplungGroup: duplungToken,
                details:
                  duplungToken ?? (ci.details ? String(ci.details).trim() : "")
              });
            });
          }
        } else {
          // --- NINCS gyerek ref → valódi leaf, ezt tesszük a listába ---
          sizes.forEach((sz) => {
            const [pw, ph, pd] = roundSizeTriple(sz);
            out.push({
              itemId: null,
              name: item.name,
              material: item.material,
              qty: Q,
              size: `[${pw},${ph},${pd}]`,
              position: posArr.map((a) => `[${a.join(",")}]`).join(","),
              rotation: rotArr.map((a) => `[${a.join(",")}]`).join(","),
              color: null,
              kant: item.kant,
              rotable: item.rotable,
              object: null,
              work,
              details: item.details,
              refSettings: item.refSettings,
              refScriptId: null,
              // ⬇⬇⬇ EZ A FIX
              groupId: null,
              instancePath: null
            });
          });
        }
      } catch (err) {
        console.error(`${indent}❌ Hiba az item feldolgozásakor:`, err, item);
      } finally {
        dgroupEnd();
      }
    });

    return out;
  }

  function dedupeAndFlatten(items) {
    dgroup("🧮 dedupeAndFlatten IN");
    // kulcs: name|material|size|kant|rotable|details|colorId (szándékosan NEM vesszük bele a groupId-t)
    const keyOf = (it) =>
      [
        it.name ?? "",
        it.material ?? "",
        it.size ?? "",
        it.kant ?? "",
        it.rotable ? 1 : 0,
        it.details ?? "",
        it.color ?? it.colorId ?? ""
      ].join("|");

    const map = new Map();

    items.forEach((it) => {
      const key = keyOf(it);
      const posArr = parseBracketedArrays(it.position);
      const rotArr = parseBracketedArrays(it.rotation);
      const groupIdArr = extractRawList(it.groupId);
      const explicit = Number(it.qty);
      const qty =
        Number.isFinite(explicit) && explicit > 0
          ? explicit
          : Math.max(posArr.length, rotArr.length, 1);
      dlog(`  [${it}]`, {
        name: it.name,
        size: it.size,
        qty: it.qty,
        posLen: posArr.length,
        rotLen: rotArr.length
      });

      if (!map.has(key)) {
        map.set(key, {
          ...it,
          qty: 0,
          _posList: [],
          _rotList: [],
          _groupIdList: [],
          instancePaths: []
        });
      }
      const acc = map.get(key);
      acc.qty += qty;
      if (it.instancePath) {
        acc.instancePaths.push(it.instancePath);
      }
      // hozzácsapjuk a pozíció/rotáció listákat (ha üres, 0-val töltjük)
      for (let i = 0; i < qty; i++) {
        acc._posList.push(
          `[${(posArr[i] || posArr[0] || [0, 0, 0]).join(",")}]`
        );
        acc._rotList.push(
          `[${(rotArr[i] || rotArr[0] || [0, 0, 0]).join(",")}]`
        );
        if (groupIdArr.length > 0) {
          acc._groupIdList.push(groupIdArr[i] || groupIdArr[0]);
        }
      }
    });

    return Array.from(map.values()).map((it) => {
      return {
        ...it,
        position: it._posList.join(","),
        rotation: it._rotList.join(","),
        groupId: it._groupIdList.length ? it._groupIdList.join(",") : null
      };
    });
  }

  const raw = [];
  let rootInstanceCounter = 0;
  let groupRootCounter = 0;
  raw.push(
    ...gen(
      scriptId,
      { ...currentConfig, width, height, depth },
      [scriptId],
      [++rootInstanceCounter], // ← EZ A LÉNYEG
      ++groupRootCounter
    )
  );
  const res = dedupeAndFlatten(raw);
  console.log("🛠 processScript – input & output:", __ps_in, res);
  return res;
}

export default processScript;

const evalArr = (raw, overrideSettings, overrideDims) =>
  baseEvalArr(raw, overrideSettings, overrideDims);
